import { Box, FormControl, FormControlLabel, FormGroup, Grid, IconButton, InputLabel, MenuItem, Select, styled, Table, TableBody, TableCell, TableHead, TableRow, TextField, withStyles } from '@material-ui/core';
import cleanRegExp from 'clean-regexp';
import React, { useCallback, useState } from 'react';
import { useTableScroll } from './utils';
import { SmartImage } from 'components/account/jobs/image-slider/smart-image';
import { StyledSwitch } from 'components/account/jobs/utils';
const LargeTableCell = styled(TableCell)({
    fontSize: '1.3rem'
});
const isRegexValid = (s) => {
    try {
        // eslint-disable-next-line no-new
        new RegExp(s);
        return true;
    }
    catch {
        return false;
    }
};
const simplifyRegex = (s) => cleanRegExp(s).replaceAll(/\((.)\)/g, '$1');
const StyledRegexInput = withStyles({
    root: {
        '& input,label': {
            fontFamily: 'monospace !important'
        }
    }
})(TextField);
const checkRegex = (s) => {
    if (!isRegexValid(s)) {
        return {
            valid: false,
            messages: ['Regex is invalid.']
        };
    }
    const cleanedRegExp = simplifyRegex(s);
    const hasCleanVersion = cleanedRegExp !== s;
    const out = {
        valid: true,
        messages: []
    };
    if (hasCleanVersion) {
        out.messages.push(`Can be simplified to ${cleanedRegExp}`);
    }
    if (s.includes('  ')) {
        out.messages.push(String.raw `Double whitespaces are hazardous, prefer using \s+`);
    }
    return out;
};
const TemplateValuesTableRow = ({ currentPointIndex, pointIndex, listPoint, onMouseExit, onMouseEnter, onDelete, onRegexChange, onMatchChange, cropActive, onCrop }) => {
    const handleMouseEnter = useCallback(() => {
        onMouseEnter(pointIndex);
    }, [onMouseEnter, pointIndex]);
    const handleRegexChanged = useCallback((event) => {
        onRegexChange(pointIndex, event.currentTarget.value);
    }, [onRegexChange, pointIndex]);
    const handleDelete = useCallback(() => {
        onDelete(pointIndex);
    }, [pointIndex, onDelete]);
    const handleChangeMatch = useCallback((event) => {
        onMatchChange(pointIndex, event.currentTarget.value);
    }, [onMatchChange, pointIndex]);
    const handleCrop = useCallback(() => {
        onCrop(pointIndex);
    }, [pointIndex, onCrop]);
    const checkResult = checkRegex(listPoint.regex ?? '');
    return (React.createElement(TableRow, { className: `template-values-cell ${currentPointIndex === pointIndex ? 'highlight' : null}`, onMouseEnter: handleMouseEnter, onMouseLeave: onMouseExit },
        React.createElement(LargeTableCell, { style: { fontSize: '1.5rem' } }, listPoint.word),
        React.createElement(LargeTableCell, null,
            React.createElement(StyledRegexInput, { className: "g-full-width u-margin-bottom-small", error: !checkResult.valid, helperText: !!checkResult.messages.length && (React.createElement("div", { style: {
                        color: checkResult.valid ? 'darkorange' : 'red',
                        fontSize: '10px'
                    } }, checkResult.messages.map((s) => (React.createElement("div", { key: s },
                    "- ",
                    s))))), inputProps: {
                    style: {
                        fontFamily: 'monospace'
                    }
                }, label: "Regex", onChange: handleRegexChanged, value: listPoint.regex ?? '' })),
        React.createElement(LargeTableCell, null,
            React.createElement(TextField, { className: "g-full-width u-margin-bottom-small", label: "Match", onChange: handleChangeMatch, value: listPoint.match ?? '' })),
        React.createElement(LargeTableCell, null,
            React.createElement(IconButton, { className: "fa fa-trash", onClick: handleDelete, title: "Delete" }),
            ' ',
            React.createElement(IconButton, { className: `fa fa-crop-alt ${cropActive ? 'on' : ''}`, onClick: handleCrop, title: "Select area" }))));
};
export const TemplateValuesForm = ({ onAddNewWord, dropdownValues, editWord, handleAdditionalValueChange, listPoints, setDrawRectangleMode, setEditWord, setListPoints, toggleValues, drawRectangleMode, newWord, overwriteRectangle, photoDimensions, photoUrl, setNewWord, layout }) => {
    const [currentPointIndex, setCurrentPointIndex] = useState(null);
    const [currentRowIndex, setCurrentRowIndex] = useState();
    const handleListPointRegexChange = useCallback((index, val) => {
        setListPoints((old) => {
            const newPoints = [...old];
            newPoints[index].regex = val;
            return newPoints;
        });
    }, []);
    const handleListPointMatchChange = (index, val) => {
        const newPoints = [...listPoints];
        newPoints[index].match = val;
        setListPoints(newPoints);
    };
    const handleToggleChange = (event) => {
        const newValueChange = {
            [event.target.name]: event.target.checked
        };
        handleAdditionalValueChange(newValueChange);
    };
    const handleInputChange = (event) => {
        if (!event.target.name) {
            return;
        }
        const { value, name } = event.target;
        if (name === 'presetExpirationDay' || name === 'presetExpirationMonth') {
            console.log(name);
            handleAdditionalValueChange({
                presetExpiration: {
                    ...(name === 'presetExpirationDay' &&
                        (value === '' ? { day: null } : { day: value })),
                    ...(name === 'presetExpirationMonth' &&
                        (value === '' ? { month: null } : { month: value }))
                }
            });
        }
        else {
            const newValueChange = {
                [name]: value === '' ? null : value
            };
            handleAdditionalValueChange(newValueChange);
        }
    };
    const toggleForm = Object.keys(toggleValues).map((k) => {
        return (React.createElement(FormControlLabel, { color: "primary", control: React.createElement(StyledSwitch, { checked: toggleValues[k], name: k, onChange: handleToggleChange }), key: k, label: k }));
    });
    const selectForm = Object.entries(dropdownValues.values).map(([k, val]) => {
        return (React.createElement(FormControl, { key: k },
            React.createElement(InputLabel, null, k),
            React.createElement(Select, { name: k, onChange: handleInputChange, value: val ?? '' },
                React.createElement(MenuItem, { value: "" },
                    React.createElement("em", null, "None")),
                dropdownValues.options[k].map((option, idx) => (React.createElement(MenuItem, { key: idx, value: option }, option))))));
    });
    const tableBodyRef = useTableScroll(currentPointIndex, layout === 'row');
    const handleRowMouseExit = useCallback(() => {
        setCurrentRowIndex(undefined);
    }, []);
    const handleDelete = useCallback((pointIndex) => {
        setListPoints((p) => p.filter((_val, idx) => idx !== pointIndex));
    }, []);
    const handleCrop = useCallback((pointIndex) => {
        if (pointIndex === editWord) {
            setEditWord(null);
            setDrawRectangleMode(false);
        }
        else {
            setEditWord(pointIndex);
            setListPoints((old) => {
                const newPoints = [...old];
                newPoints[pointIndex].points = [];
                return newPoints;
            });
            setDrawRectangleMode(true);
        }
    }, [editWord]);
    return (React.createElement(Grid, { container: true, direction: layout, spacing: 2, wrap: "nowrap" },
        React.createElement(Grid, { item: true },
            React.createElement(SmartImage, { className: layout === 'row' ? 'sticky-image' : '', editIndex: editWord, highlightIndex: currentRowIndex, image: {
                    dimensions: photoDimensions,
                    url: photoUrl
                }, listPoints: listPoints.map((point, idx) => point.points.map((pointPoints) => ({
                    ...pointPoints,
                    widx: idx
                }))), maxHeightPerc: 0.7, noPoints: false, onMouseOutPoint: (pts) => {
                    if (pts.length) {
                        setCurrentPointIndex(null);
                    }
                }, onMouseOverPoint: (pts) => {
                    if (pts.length) {
                        setCurrentPointIndex(pts[0].widx);
                    }
                }, readonly: !drawRectangleMode, twoPointClick: true, updatePoints: overwriteRectangle })),
        React.createElement(Grid, { item: true },
            React.createElement(Box, { marginBottom: 6 },
                React.createElement(FormGroup, { style: { marginBottom: '24px' } },
                    React.createElement(TextField, { InputProps: {
                            endAdornment: (React.createElement(IconButton, { className: "fa fa-plus fa-solid", onClick: onAddNewWord, title: "Add new word" }))
                        }, className: "g-full-width u-margin-bottom-small", label: "Field name", onChange: (e) => {
                            setNewWord(e.target.value);
                        }, value: newWord }))),
            React.createElement(Table, { padding: "none", size: "medium", stickyHeader: true, style: {
                    fontSize: '2rem'
                } },
                React.createElement(TableHead, null,
                    React.createElement(TableRow, null,
                        React.createElement(LargeTableCell, null, "Name"),
                        React.createElement(LargeTableCell, null, "Regex"),
                        React.createElement(LargeTableCell, null, "Match"),
                        React.createElement(LargeTableCell, null, "Actions"))),
                React.createElement(TableBody, { ref: tableBodyRef }, listPoints.map((listPoint, pointIndex) => (React.createElement(TemplateValuesTableRow, { cropActive: editWord === pointIndex, currentPointIndex: currentPointIndex, 
                    // eslint-disable-next-line react/no-array-index-key
                    key: pointIndex, listPoint: listPoint, onCrop: handleCrop, onDelete: handleDelete, onMatchChange: handleListPointMatchChange, onMouseEnter: setCurrentRowIndex, onMouseExit: handleRowMouseExit, onRegexChange: handleListPointRegexChange, pointIndex: pointIndex }))))),
            React.createElement(FormGroup, { row: false }, selectForm),
            React.createElement(FormGroup, { row: true }, toggleForm),
            React.createElement(FormGroup, { row: true },
                React.createElement("a", { href: "https://docs.google.com/spreadsheets/d/1h3VepHOIWH1j0fN_qJlZ2Bb64BfZLG67P9lC216Jxzo/edit#gid=0", rel: "noreferrer", target: "_blank" }, "Cheat sheet")))));
};
